import React, { useEffect, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { trackEvent } from '@app/services/analytics.service';
import { readLocalStorageData } from '@app/services/localStorage.service';
import { notificationService } from '@app/services/notification.service';
import { LocalStorageItems } from '@app/shared/constants/localStorageItems';
import { doLogin, doLogout } from '@app/store/slices/authSlice';
import { resetStore } from '@app/store/store';
import ChangeLanguage from '@FW/components/Language/LanguageRadio/ChangeLanguage';
import { useLoginRedirect } from '@FW/hooks/useLoginRedirect';
import { useAppDispatch } from '@hooks/shared/reduxHooks';
import { StdApiError } from '@interfaces/FW/StdApiError';
import { Alert, Button, Divider, Flex, Form, Input, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthWrap } from './AuthWrap.styled';
import LoginByPhoneForm from './LoginByPhoneForm';
import { LegacyPanel } from './LoginScreen.styled';

const { Text } = Typography;

type LegacyFormData = {
	username: string;
	password: string;
};

/** Which sign-in is in flight — the two share a handler but not a spinner. */
type PendingLogin = 'legacy' | 'demo';

const LoginForm: React.FC = () => {
	const [legacyOpen, setLegacyOpen] = useState(false);
	const [pending, setPending] = useState<PendingLogin | null>(null);
	const [formError, setFormError] = useState<string | null>(null);

	const navigate = useNavigate();
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const redirectAfterLogin = useLoginRedirect();

	/**
	 * If user comes here - delete all the data and log him out.
	 */
	useEffect(() => {
		if (readLocalStorageData(LocalStorageItems.isAuth)) {
			dispatch(doLogout()).finally(() => {
				dispatch(resetStore());
			});
		}
	}, []);

	/**
	 * `getErrorData()` maps every 401 to the session-expiry copy. On this screen a
	 * 401 from `Login` only ever means the credentials were rejected.
	 */
	const resolveLoginError = (err: StdApiError): string => {
		if (err.status === 401) return t('auth.invalidCredentials');

		return err.message ? err.message : t('api.error');
	};

	/** Toggling the panel drops any error from a previous attempt. */
	const toggleLegacy = (open: boolean) => {
		setLegacyOpen(open);
		setFormError(null);
	};

	const handleLogin = (
		values: LegacyFormData,
		action: PendingLogin,
		onFail: (message: string) => void,
	) => {
		setPending(action);
		setFormError(null);

		dispatch(doLogin(values))
			.unwrap()
			.then(
				(data) => {
					if (data.needSecondStep) {
						navigate('/auth/verification');
						return;
					}

					// Demo sessions skip the real funnel: counting them as completions would
					// put completions in the chart with no attempt behind them.
					if (action === 'demo') {
						trackEvent('demo_login', {});
						return redirectAfterLogin();
					}

					return redirectAfterLogin({ loginMethod: 'password' });
				},
				// Rejection handler rather than `.catch`: only the login request itself is
				// a password failure. The redirect above can fail too (loading the client
				// list), and that must not be reported as one.
				(err: StdApiError) => {
					if (action === 'legacy') {
						trackEvent('login', {
							login_status: err.status === 401 ? 'password_wrong' : 'password_failed',
							login_method: 'password',
						});
					}
					onFail(resolveLoginError(err));
				},
			)
			.catch((err: StdApiError) => onFail(err.message ? err.message : t('api.error')))
			.finally(() => setPending(null));
	};

	return (
		<Flex vertical gap={16} className='w-full'>
			<AuthWrap>
				<Flex vertical gap={24}>
					<Flex vertical gap={8}>
						<Title level={3} style={{ margin: 0 }}>
							{t('auth.loginFormTitle')}
						</Title>
						<Text type='secondary'>{t('auth.loginIntro')}</Text>
					</Flex>

					<LoginByPhoneForm />

					<Divider style={{ margin: 0 }} />

					{!legacyOpen ? (
						<Flex vertical gap={8} align='flex-start'>
							<Button
								type='dashed'
								aria-expanded={false}
								aria-describedby='legacy-login-note'
								onClick={() => toggleLegacy(true)}>
								{t('auth.loginByPassword')}
							</Button>

							<Text id='legacy-login-note' type='secondary'>
								{t('auth.passwordLoginDeprecated')}
							</Text>
						</Flex>
					) : (
						<LegacyPanel id='legacy-login-panel'>
							<Flex vertical gap={20}>
								<Flex justify='space-between' align='center'>
									<Text strong>
										<DownOutlined style={{ fontSize: 12 }} /> {t('auth.legacySectionTitle')}
									</Text>
									<Button
										type='dashed'
										aria-expanded
										aria-controls='legacy-login-panel'
										onClick={() => toggleLegacy(false)}>
										{t('auth.collapse')}
									</Button>
								</Flex>

								<Alert type='error' description={t('auth.passwordLoginDeprecated')} />

								<Form
									layout='vertical'
									size='large'
									requiredMark={false}
									onFinish={(values: LegacyFormData) =>
										handleLogin(values, 'legacy', setFormError)
									}>
									<Flex vertical gap={20}>
										<Form.Item<LegacyFormData>
											name='username'
											label={t('auth.usernamePlaceholder')}
											style={{ marginBottom: 0 }}
											rules={[{ required: true, message: t('auth.usernameError') }]}>
											<Input autoFocus autoComplete='username' />
										</Form.Item>

										<Form.Item<LegacyFormData>
											name='password'
											label={t('auth.passwordPlaceholder')}
											style={{ marginBottom: 0 }}
											extra={
												<Button
													type='link'
													className='text-green p-0--i'
													onClick={() => navigate('/auth/passwordRecovery')}>
													{t('auth.forgotPassword')}
												</Button>
											}
											rules={[{ required: true, message: t('auth.passwordError') }]}>
											<Input.Password autoComplete='current-password' />
										</Form.Item>

										{formError && <Alert type='error' showIcon message={formError} />}

										<Button htmlType='submit' block loading={pending === 'legacy'}>
											{t('auth.buttonText')}
										</Button>
									</Flex>
								</Form>
							</Flex>
						</LegacyPanel>
					)}

					<Divider style={{ margin: 0 }} />

					<Flex wrap gap='8px 24px'>
						<Button
							type='link'
							className='p-0--i text-gray'
							style={{ fontWeight: 600 }}
							onClick={() => navigate('/registration')}>
							{t('auth.signUpButtonText')}
						</Button>
						<Button
							type='link'
							className='p-0--i text-gray'
							loading={pending === 'demo'}
							onClick={() =>
								handleLogin({ username: 'demo', password: 'demo' }, 'demo', (message) =>
									notificationService.error({ message }),
								)
							}>
							{t('auth.loginDemo')}
						</Button>
						<Button type='link' className='p-0--i text-gray' onClick={() => navigate('/faq')}>
							{t('footer.faq')}
						</Button>
						<Button type='link' className='p-0--i text-gray' onClick={() => navigate('/api-docs')}>
							{t('footer.apiManual')}
						</Button>
					</Flex>
				</Flex>
			</AuthWrap>

			<ChangeLanguage />
		</Flex>
	);
};

export default LoginForm;
